import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { Row, Col } from 'antd';
import { useLocation, useHistory } from 'react-router-dom';
import { DateTime } from 'luxon';

import TimeCardsContainer from './TimeCardsContainer';
import TeamMembersPanel from './TeamMembersPanel/TeamMembersPanel';
import { activeUserFilterModeToSet } from './TeamMembersPanel/teamMembersActiveFilter.helpers';
import TeamTimeCardToolbar from './TeamTimeCardToolbar';

import { search, mergeSets, getIdMap } from '../helpers/helpers';
import Permissions from '../auth/Permissions';

import TimeTracking from '../state/timeTracking';

import { getTimesFromDisplayTimeRange } from './timecard.helpers';

import { updateCurrentTimeEntriesRange } from './state/timecards.actions';

const DEFAULT_VIEW = 'default';
const SUBMISSION_VIEW = 'submitted';
const ACTIVE_FILTER_KEY = 'active';
const TEAM_FILTER_KEY = 'teams';
const DEFAULT_ACTIVE_FILTER = new Set([1]);
const EMPTY_SET = new Set();

const getUserIdKey = (userList) => userList.map(({ id }) => id).sort().join(',');

function TeamTimeCardContainer({
  onUserSelect: propUserSelect,
}) {
  const dispatch = useDispatch();
  const history = useHistory();
  const {
    state: { submissions: showSubmissions, user: locationStateUser } = {},
  } = useLocation();

  const approvalCount = useSelector((state) => state.approvals.approvalCount);
  const settings = useSelector((state) => state.settings.company?.settings);
  const selectedDivisions = useSelector((state) => state.settings.selectedDivisions);
  const divisions = useSelector((state) => state.settings.divisions);
  const users = useSelector((state) => state.users.users);
  const teams = useSelector((state) => state.teams.teams);
  const approvals = useSelector((state) => state.approvals.approvals);
  const isFullScreen = useSelector((state) => state.main.menuCollapsed);
  const {
    timeRange,
  } = useSelector((state) => state.timecards);

  const [selectedSubmitView, setSelectedSubmitView] = useState(DEFAULT_VIEW);
  const [selectedUser, setSelectedUser] = useState();
  const [searchTerm, setSearchTerm] = useState('');
  const [panelCollapsed, setPanelCollapsed] = useState(false);
  const [loadingId, setLoadingId] = useState(false);
  // No teams checked means users are not filtered by team
  const [activeFilters, setActiveFilters] = useState({
    [ACTIVE_FILTER_KEY]: DEFAULT_ACTIVE_FILTER,
  });

  const {
    enableTimecardSubmission,
  } = settings;

  const onSearch = useCallback((value) => {
    setSearchTerm(value);
  }, []);

  const onTeamChange = useCallback((teamIds) => {
    setActiveFilters((oldFilters) => ({
      ...oldFilters,
      [TEAM_FILTER_KEY]: new Set(teamIds ?? []),
    }));
  }, []);

  const onActiveUserFilterChange = useCallback((mode) => {
    setActiveFilters((oldFilters) => ({
      ...oldFilters,
      [ACTIVE_FILTER_KEY]: activeUserFilterModeToSet(mode),
    }));
  }, []);

  const activeUserFilterSet = activeFilters[ACTIVE_FILTER_KEY] ?? DEFAULT_ACTIVE_FILTER;

  const onTogglePanel = useCallback(() => {
    setPanelCollapsed((collapsed) => !collapsed);
  }, []);

  const isApprovals = useMemo(() => selectedSubmitView === SUBMISSION_VIEW, [selectedSubmitView]);
  const approvalSet = useMemo(() => new Set(approvals.map(({ taskId }) => taskId)), [approvals]);
  const submissionSet = useMemo(() => new Set(approvals.map(({ userId }) => userId)), [approvals]);

  const {
    ourTeams: parsedTeams,
    userToTeamMap: userTeamMap,
  } = useMemo(() => {
    const ourTeams = [];
    const userToTeamMap = {};

    teams.forEach((team) => {
      if (!team.active || team?.divisionIds?.every((dId) => !selectedDivisions.has(dId))) return;
      const {
        members = [],
      } = team;

      members.forEach((member) => {
        const {
          id: userId,
          teamRole,
        } = member;

        if (!userToTeamMap[userId]) {
          userToTeamMap[userId] = [];
        }

        userToTeamMap[userId].push({
          teamId: team.id,
          role: teamRole,
        });
      });

      ourTeams.push({
        ...team,
        memberSet: new Set(members.map((member) => member.id)),
      });
    });

    return {
      ourTeams,
      userToTeamMap,
    };
  }, [teams, selectedDivisions]);

  const doesSuperviseUser = useCallback((userId) => {
    const userTeams = userTeamMap[userId];
    if (!userTeams) return false;
    const currentUserTeams = userTeamMap[Permissions.id];
    if (!currentUserTeams) return false;

    const currentUserTeamMap = getIdMap(currentUserTeams, 'teamId');

    return userTeams.some(({ teamId, role }) => (
      currentUserTeamMap[teamId]?.role === 'Supervisor' && role !== 'Supervisor'
    ));
  }, [userTeamMap]);

  const selectedTeamSet = activeFilters[TEAM_FILTER_KEY] ?? EMPTY_SET;

  /**
   * Set of user ids belonging to the checked teams.
   * Null when no team is checked, meaning users are not filtered by team.
   */
  const selectedTeamMemberSet = useMemo(() => {
    if (selectedTeamSet.size === 0) return null;
    const selectedTeams = parsedTeams.filter(({ id }) => selectedTeamSet.has(id));
    return mergeSets(selectedTeams.map(({ memberSet }) => memberSet));
  }, [parsedTeams, selectedTeamSet]);

  const fullUsers = useMemo(() => {
    const activeFilterSet = activeFilters[ACTIVE_FILTER_KEY] ?? EMPTY_SET;
    const userDivSets = mergeSets(
      Object.values(divisions)
        .filter(({ id: divisionId }) => selectedDivisions.has(divisionId))
        .map(({ users: divUsers }) => divUsers),
    );

    const hasPermissionForUser = (user) => (
      Permissions.hasForUser(user.id, user.position, 'TIMECARD_VIEW')
      || doesSuperviseUser(user.id)
    );

    const usersWithAccess = users.filter((user) => (
      activeFilterSet.has(user.active ? 1 : 0)
      && userDivSets.has(user.id)
      && (!selectedTeamMemberSet || selectedTeamMemberSet.has(user.id))
      && hasPermissionForUser(user)
    ));

    return usersWithAccess;
  }, [
    activeFilters,
    selectedTeamMemberSet,
    divisions,
    selectedDivisions,
    users,
    doesSuperviseUser,
  ]);

  const relevantUsers = useMemo(() => (
    fullUsers?.filter((user) => (
      selectedSubmitView === DEFAULT_VIEW || submissionSet.has(user.id)
    )) ?? []
  ), [fullUsers, selectedSubmitView, submissionSet]);

  const relevantUsersWithSummary = useMemo(() => (
    [{ name: 'Summary', id: 'summary' }].concat(relevantUsers)
  ), [relevantUsers]);

  const relevantUserKey = useMemo(() => getUserIdKey(relevantUsers), [relevantUsers]);

  const filteredUsers = useMemo(() => (
    search({
      data: relevantUsersWithSummary,
      value: searchTerm,
      getField: (user) => user.name,
    })
  ), [relevantUsersWithSummary, searchTerm]);

  // Tracks the users the summary time entries were last fetched for
  const loadedSummaryUserKey = useRef();

  const loadTime = useCallback(({
    newUser,
    newRange = null,
    newSubmitView = null,
    newRelevantUsers = null,
  }) => {
    if (!newUser?.id || newUser.id === loadingId) return;
    const isSummary = newUser?.id === 'summary';
    const payload = { getRunningTasks: true };

    if (isSummary) {
      const relevantUsersToUse = newRelevantUsers ?? relevantUsers;
      if (!relevantUsersToUse?.length) return;
      payload.userIds = relevantUsersToUse.map((u) => u.id);
      loadedSummaryUserKey.current = getUserIdKey(relevantUsersToUse);
    } else {
      payload.userId = newUser.id;
    }

    const timeRangeToUse = newRange ?? timeRange;
    let finalIsApprovals = isApprovals;
    if (newSubmitView === SUBMISSION_VIEW) {
      finalIsApprovals = true;
    } else if (newSubmitView === DEFAULT_VIEW) {
      finalIsApprovals = false;
    }
    if (!finalIsApprovals) {
      const { startTime: ourStart, endTime: ourEnd } = getTimesFromDisplayTimeRange(timeRangeToUse);
      payload.startTime = ourStart;
      payload.endTime = ourEnd;
    } else {
      payload.ids = Array.from(approvalSet);
      payload.removeIds = true;
    }

    payload.getCardDetails = true;

    setLoadingId(newUser.id);
    dispatch(TimeTracking.getTimeEntries(payload));
    if (payload.startTime && payload.endTime) {
      dispatch(updateCurrentTimeEntriesRange(
        DateTime.fromMillis(payload.startTime),
        DateTime.fromMillis(payload.endTime),
      ));
    }
    setLoadingId(false);
  }, [isApprovals, relevantUsers, approvalSet, loadingId, timeRange]);

  const onSubmitViewChange = useCallback(async (view) => {
    setSelectedSubmitView(view);
    const newRelevantUsers = fullUsers?.filter((user) => (
      view === SUBMISSION_VIEW ? submissionSet.has(user.id) : true
    )) ?? [];
    await loadTime({
      newUser: selectedUser,
      newSubmitView: view,
      newRelevantUsers,
    });
  }, [selectedUser, fullUsers, submissionSet, loadTime]);

  const onUserSelected = useCallback(async (user) => {
    const {
      name,
    } = user;
    await loadTime({ newUser: user });
    propUserSelect(name);
    setSelectedUser(user);
  }, [propUserSelect, loadTime]);

  const onDateRangeChange = useCallback((newRange) => {
    loadTime({ newUser: selectedUser, newRange });
  }, [selectedUser, loadTime]);

  useEffect(() => {
    if (
      // Wait for relevant users to be loaded
      relevantUsersWithSummary.length > 1
      && (
        !selectedUser
        || !relevantUsersWithSummary.some((user) => user.id === selectedUser.id)
      )
    ) {
      onUserSelected(relevantUsersWithSummary[0]);
    }
  }, [relevantUsersWithSummary, selectedUser, onUserSelected]);

  useEffect(() => {
    if (
      selectedUser?.id !== 'summary'
      || !relevantUsers.length
      || loadedSummaryUserKey.current === relevantUserKey
    ) return;
    loadTime({ newUser: selectedUser });
  }, [selectedUser, relevantUsers, relevantUserKey, loadTime]);

  useEffect(() => {
    if (locationStateUser) {
      const ourUser = relevantUsers.find((user) => user.id === locationStateUser);
      if (ourUser) {
        onUserSelected(ourUser);
        setSelectedSubmitView(showSubmissions ? SUBMISSION_VIEW : DEFAULT_VIEW);
        history.replace({
          pathname: '/timecards/team',
          state: {},
        });
      }
    }
  }, [locationStateUser, showSubmissions, onUserSelected, relevantUsers]);

  const selectedTeamIds = useMemo(
    () => Array.from(selectedTeamSet),
    [selectedTeamSet],
  );

  const showSubmissionHeader = Permissions.has('TEAMS_WRITE') && enableTimecardSubmission;

  return (
    <Row style={{ height: '100%', width: '100%', flexWrap: 'nowrap' }}>
      {!isFullScreen && (
        <Col
          style={{
            height: '100%',
            paddingRight: 10,
            width: panelCollapsed ? 0 : '25%',
            maxWidth: panelCollapsed ? 0 : '25%',
            flex: panelCollapsed ? '0' : '0 0 280px',
            overflow: 'hidden',
          }}
        >
          <TeamMembersPanel
            users={filteredUsers}
            selectedUser={selectedUser}
            onSelect={onUserSelected}
            teams={parsedTeams}
            selectedTeamIds={selectedTeamIds}
            onTeamChange={onTeamChange}
            activeUserFilterSet={activeUserFilterSet}
            onActiveUserFilterChange={onActiveUserFilterChange}
            searchValue={searchTerm}
            onSearch={onSearch}
            showSubmissionTabs={showSubmissionHeader}
            submitView={selectedSubmitView}
            onSubmitViewChange={onSubmitViewChange}
            approvalCount={approvalCount}
            loadingId={loadingId}
            collapsed={panelCollapsed}
            onToggleCollapsed={onTogglePanel}
            submittedView={SUBMISSION_VIEW}
            defaultView={DEFAULT_VIEW}
          />
        </Col>
      )}
      <Col
        span={isFullScreen ? 24 : undefined}
        style={{
          height: '100%',
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          flex: isFullScreen ? undefined : '1 1 auto',
          paddingLeft: isFullScreen || panelCollapsed ? 0 : 10,
        }}
      >
        <TeamTimeCardToolbar
          panelCollapsed={panelCollapsed}
          onTogglePanelCollapsed={onTogglePanel}
        />
        {selectedUser && (
          <div style={{ flex: 1, minHeight: 0, minWidth: 0 }}>
            <TimeCardsContainer
              userId={selectedUser?.id === 'summary' ? null : selectedUser.id}
              isApprovals={isApprovals}
              relevantUsers={relevantUsers}
              fullUsers={fullUsers}
              isSummary={selectedUser?.id === 'summary'}
              onDateRangeChange={onDateRangeChange}
              sideListUsers={filteredUsers}
              sideListUserId={selectedUser?.id}
              onSideListUserSelect={onUserSelected}
              sideListNavigationDisabled={Boolean(loadingId)}
            />
          </div>
        )}
      </Col>
    </Row>
  );
}

TeamTimeCardContainer.propTypes = {
  onUserSelect: PropTypes.func.isRequired,
};

export default TeamTimeCardContainer;
